// src/features/fx-info/components/FxTimeSeriesChart.tsx
//
// Pillar 3 — Master synchronized time-series.
// Price, Drawdown and AFML Fractional-Differencing run on totally different
// axes and scales, so we stack three separate ResponsiveContainers vertically.
// By sharing syncId="fx-master-sync", Recharts propagates hover/crosshair
// events across every pane (including the COT chart), so hovering a price peak
// snaps the crosshair to the exact drawdown and stationary-memory value for
// that day. Right-aligned Y-axes with a fixed width keep the panes perfectly
// left-aligned even as tick decimals change.
//
// Performance discipline for ~1,300+ daily rows: dot={false} +
// isAnimationActive={false} guarantee instant 60fps rendering (no thousands of
// SVG dots, no re-layout animations).

import { useMemo, useRef } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ComposedChart,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { FxTimeSeriesPoint } from '../api';
import { ChartDownloadMenu } from '../../../shared/ui';
import { useChartExport } from '../../../shared/hooks/useChartExport';
import { buildChartFilename } from '../../../shared/utils/chartFilename';

interface Props {
    data: FxTimeSeriesPoint[];
    symbol: string;
    invertPrice: boolean;
}

export const FxTimeSeriesChart = ({ data, symbol, invertPrice }: Props) => {
    const captureRef = useRef<HTMLDivElement>(null);
    const { downloadPng, downloadJpeg } = useChartExport(captureRef);

    // Add formatted dates for nicer tooltips / X-axis labels.
    const chartData = useMemo(
        () =>
            data.map((d) => ({
                ...d,
                displayDate: new Date(d.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                }),
                axisDate: new Date(d.date).toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit',
                }),
            })),
        [data],
    );

    if (!data.length) return null;

    const formatPrice = (val: number) => val.toFixed(4);
    const formatPct = (val: number) =>
        new Intl.NumberFormat('en-US', {
            style: 'percent',
            maximumFractionDigits: 1,
        }).format(val);
    const tooltipStyle = {
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '8px',
        color: '#f8fafc',
        fontSize: '13px',
    };

    const prices = data.map((d) => d.close);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const padding = (maxPrice - minPrice) * 0.05;

    return (
        <div className="w-full flex flex-col gap-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                        Trend, Drawdown & Memory
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        Synchronized {symbol} Price Action, Drawdown, and AFML
                        Stationary Memory.
                        {invertPrice
                            ? ' (Rising = USD Stronger)'
                            : ' (Rising = Local Currency Stronger)'}
                    </p>
                </div>
                <ChartDownloadMenu
                    label="Trend, Drawdown & Memory chart"
                    onPng={() => downloadPng({ filename: buildChartFilename('fx-timeseries', [symbol, invertPrice ? 'inverted' : 'normal']) })}
                    onJpeg={() => downloadJpeg({ filename: buildChartFilename('fx-timeseries', [symbol, invertPrice ? 'inverted' : 'normal']) })}
                />
            </div>

            <div ref={captureRef} className="flex flex-col gap-6 bg-white dark:bg-slate-900">
            {/* Top Chart: Price & SMAs */}
            <div className="w-full h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartData}
                        syncId="fx-master-sync"
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.2}
                        />
                        <XAxis
                            dataKey="axisDate"
                            minTickGap={50}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            domain={[minPrice - padding, maxPrice + padding]}
                            tickFormatter={formatPrice}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            orientation="right"
                            width={60}
                        />
                        <Tooltip
                            contentStyle={tooltipStyle}
                            labelFormatter={(label, payload) =>
                                payload?.[0]?.payload?.displayDate || label
                            }
                            formatter={(val: number) => [formatPrice(val), '']}
                        />
                        <Line
                            type="monotone"
                            dataKey="close"
                            name="Close"
                            stroke="#6366f1"
                            strokeWidth={1.5}
                            dot={false}
                            isAnimationActive={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="sma_50"
                            name="50d SMA"
                            stroke="#94a3b8"
                            strokeWidth={1.5}
                            dot={false}
                            isAnimationActive={false}
                            connectNulls
                        />
                        <Line
                            type="monotone"
                            dataKey="sma_200"
                            name="200d SMA"
                            stroke="#f59e0b"
                            strokeWidth={1.5}
                            strokeDasharray="4 4"
                            dot={false}
                            isAnimationActive={false}
                            connectNulls
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Middle Chart: Drawdown */}
            <div className="w-full h-[120px]">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 ml-4">
                    Historical Drawdown
                </p>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        syncId="fx-master-sync"
                        margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.2}
                        />
                        <XAxis dataKey="axisDate" hide />
                        <YAxis
                            domain={['auto', 0]}
                            tickFormatter={formatPct}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            orientation="right"
                            width={60}
                        />
                        <Tooltip
                            contentStyle={tooltipStyle}
                            labelFormatter={(label, payload) =>
                                payload?.[0]?.payload?.displayDate || label
                            }
                            formatter={(val: number) => [
                                formatPct(val),
                                'Drawdown',
                            ]}
                        />
                        <Area
                            type="step"
                            dataKey="drawdown"
                            stroke="#ef4444"
                            fill="#ef4444"
                            fillOpacity={0.2}
                            isAnimationActive={false}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            {/* Bottom Chart: Fractional Differencing */}
            <div className="w-full h-[120px]">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 ml-4 flex justify-between pr-10">
                    <span>Stationary Memory (Frac-Diff)</span>
                    <span className="font-mono bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                        d=0.45
                    </span>
                </p>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        syncId="fx-master-sync"
                        margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.2}
                        />
                        <XAxis dataKey="axisDate" hide />
                        <YAxis
                            domain={['auto', 'auto']}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            orientation="right"
                            width={60}
                        />
                        <Tooltip
                            contentStyle={tooltipStyle}
                            labelFormatter={(label, payload) =>
                                payload?.[0]?.payload?.displayDate || label
                            }
                            formatter={(val: number) => [
                                val.toFixed(4),
                                'Frac-Diff',
                            ]}
                        />
                        <ReferenceLine
                            y={0}
                            stroke="#475569"
                            strokeDasharray="3 3"
                        />
                        <Line
                            type="monotone"
                            dataKey="frac_diff"
                            stroke="#10b981"
                            strokeWidth={1.5}
                            dot={false}
                            isAnimationActive={false}
                            connectNulls
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            </div>
        </div>
    );
};

export default FxTimeSeriesChart;
